import {
  faCheckCircle,
  faCircle,
  faCircleQuestion,
  faCircleXmark,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { Text, View } from '@react-pdf/renderer';
import moment from 'moment';

import FontAwesomeIcon from 'src/components/custom_components/PdfFontAwesomeIcon';

import { Disease, DiseaseFullName } from 'src/models/Disease.model';
import { Eye } from 'src/models/Eye.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import {
  Grading,
  GradingIsNegative,
  GradingName,
} from 'src/models/Grading.model';
import { handleNSI18Next } from 'src/models/Language.model';
import { ReportType } from 'src/models/ReportType.model';
import { Result } from 'src/models/Result.model';
import { Session } from 'src/models/Session.model';

import { useHistoryResult } from 'src/hooks/useHistoryResult';

import { cls } from 'src/utils/reactPdfStyles';

import VersionSection from './VersionSection';

interface IResultSectionProps {
  session: Session;
  historyIndex?: number;
  showNotes: boolean;
  type: ReportType;
  currentUser: GalenDataUser;
}

const resultBadge = (type: ReportType, result: Result, oldResult?: Result) => {
  const { handleCommonT } = handleNSI18Next();

  // error?
  if (result.error) {
    return type === ReportType.CLINICAL ? (
      <View style={cls('badge rounded-pill bg-secondary')}>
        <FontAwesomeIcon
          icon={faCircleXmark}
          style={cls('me-2 text-white')}
        ></FontAwesomeIcon>

        <Text>
          {handleCommonT('exception.error')}
          {oldResult && result.error !== oldResult.error && '*'}
        </Text>
      </View>
    ) : (
      <View style={cls('badge rounded-pill text-black')}>
        <FontAwesomeIcon
          icon={faCircle}
          style={cls('me-2 text-secondary')}
        ></FontAwesomeIcon>

        <Text>
          {handleCommonT('exception.error')}
          {oldResult && result.error !== oldResult.error && '*'}
        </Text>
      </View>
    );
  }

  // ungradable?
  if (result.gradable === false) {
    return type === ReportType.CLINICAL ? (
      <View style={cls('badge rounded-pill bg-secondary')}>
        <FontAwesomeIcon
          icon={faCircleQuestion}
          style={cls('me-2 text-white')}
        ></FontAwesomeIcon>

        <Text hyphenationCallback={(word) => [word]} style={cls('me-4')}>
          {handleCommonT('exception.ungradable')}
          {oldResult && result.gradable !== oldResult.gradable && '*'}
        </Text>
      </View>
    ) : (
      <View style={cls('badge rounded-pill text-black')}>
        <FontAwesomeIcon
          icon={faCircle}
          style={cls('me-2 text-secondary')}
        ></FontAwesomeIcon>

        <Text>
          {handleCommonT('exception.ungradable')}
          {oldResult && result.gradable !== oldResult.gradable && '*'}
        </Text>
      </View>
    );
  }

  // negative?
  if (GradingIsNegative(result.grading)) {
    return type === ReportType.CLINICAL ? (
      <View style={cls('badge rounded-pill bg-success')}>
        <FontAwesomeIcon
          icon={faCheckCircle}
          style={cls('me-2 text-white')}
        ></FontAwesomeIcon>

        <Text hyphenationCallback={(word) => [word]} style={cls('me-4')}>
          {handleCommonT(`gradingName.${GradingName(result.grading)}`)}
          {oldResult &&
            result.grading.toString().toLowerCase() !==
              oldResult.grading.toString().toLowerCase() &&
            '*'}
        </Text>
      </View>
    ) : (
      <View style={cls('badge rounded-pill text-black')}>
        <FontAwesomeIcon
          icon={faCircle}
          style={cls('me-2 text-success')}
        ></FontAwesomeIcon>

        <Text>
          {handleCommonT(`gradingName.${GradingName(result.grading)}`)}
          {oldResult &&
            result.grading.toString().toLowerCase() !==
              oldResult.grading.toString().toLowerCase() &&
            '*'}
        </Text>
      </View>
    );
  }

  // positive
  const severe =
    result.grading === Grading.HIGH_RISK ||
    result.grading === Grading.PROLIFERATIVE_DR ||
    result.grading === Grading.LATE_WET ||
    result.grading === Grading.POSITIVE ||
    result.grading === Grading.SEVERE_ATHEROSCLEROSIS;

  return type === ReportType.CLINICAL ? (
    <View
      style={cls(`badge rounded-pill ${severe ? 'bg-danger' : 'bg-warning'}`)}
    >
      <FontAwesomeIcon
        icon={faTriangleExclamation}
        style={cls('me-2 text-white')}
      ></FontAwesomeIcon>

      <Text hyphenationCallback={(word) => [word]} style={cls('me-4')}>
        {handleCommonT(`gradingName.${GradingName(result.grading)}`)}
        {oldResult &&
          result.grading.toString().toLowerCase() !==
            oldResult.grading.toString().toLowerCase() &&
          '*'}
      </Text>
    </View>
  ) : (
    <View style={cls('badge rounded-pill text-black')}>
      <FontAwesomeIcon
        icon={faCircle}
        style={cls(`me-2 ${severe ? 'text-danger' : 'text-warning'}`)}
      ></FontAwesomeIcon>

      <Text>
        {handleCommonT(`gradingName.${GradingName(result.grading)}`)}
        {oldResult &&
          result.grading.toString().toLowerCase() !==
            oldResult.grading.toString().toLowerCase() &&
          '*'}
      </Text>
    </View>
  );
};

const ResultSection: React.FC<IResultSectionProps> = ({
  session,
  historyIndex,
  showNotes,
  type,
  currentUser,
}) => {
  const { handleReportT } = handleNSI18Next();
  const { getHistoryResult } = useHistoryResult(session);

  const practiceName = currentUser.currentRole?.practice?.name;
  const formattedTimestamp = historyIndex
    ? moment(session?.history?.[historyIndex]?.SavedTime?.toString()).format(
        'YYYY-MM-DD HH:mm:ss',
      )
    : '';

  const diseaseResultTr = (
    type: ReportType,
    disease: Disease,
    historyIndex?: number,
  ) => {
    // current results
    const leftResult = getHistoryResult(Eye.LEFT, disease, historyIndex);
    const rightResult = getHistoryResult(Eye.RIGHT, disease, historyIndex);

    // original results
    const originalLeftResult = getHistoryResult(Eye.LEFT, disease, 0);
    const originalRightResult = getHistoryResult(Eye.RIGHT, disease, 0);

    const { handleCommonT } = handleNSI18Next();

    return type === ReportType.CLINICAL ? (
      <View style={cls('d-flex hstack justify-content-between')}>
        <View style={cls('td w-30')}>
          <Text>
            {handleCommonT(`diseaseFullName.${DiseaseFullName(disease)}`)}:
          </Text>
        </View>
        <View style={cls('td w-20')}>
          {rightResult === undefined ? (
            <Text>{handleCommonT('exception.NA')}</Text>
          ) : (
            resultBadge(type, rightResult, originalRightResult)
          )}
        </View>

        <View style={cls('p-0 border-0')}></View>

        <View style={cls('td w-30')}>
          <Text>
            {handleCommonT(`diseaseFullName.${DiseaseFullName(disease)}`)}:
          </Text>
        </View>
        <View style={cls('td w-20')}>
          {leftResult === undefined ? (
            <Text>{handleCommonT('exception.NA')}</Text>
          ) : (
            resultBadge(type, leftResult, originalLeftResult)
          )}
        </View>
      </View>
    ) : (
      <View style={cls('d-flex hstack justify-content-between')}>
        <View style={cls('td w-20 border-top-0')}>
          <Text>
            {handleCommonT(`diseaseFullName.${DiseaseFullName(disease)}`)}
          </Text>
        </View>
        <View style={cls('td w-40 border-top-0 dashed')}>
          {rightResult === undefined ? (
            <Text>{handleCommonT('exception.NA')}</Text>
          ) : (
            resultBadge(type, rightResult, originalRightResult)
          )}
        </View>
        <View style={cls('td w-40 border-top-0 dashed')}>
          {leftResult === undefined ? (
            <Text>{handleCommonT('exception.NA')}</Text>
          ) : (
            resultBadge(type, leftResult, originalLeftResult)
          )}
        </View>
      </View>
    );
  };

  return type === ReportType.CLINICAL ? (
    <>
      <View style={cls('d-flex hstack justify-content-between')}>
        <View style={cls('td w-30 text-muted')}>
          <Text>{handleReportT(`resultSection.diseaseType`)}</Text>
        </View>
        <View style={cls('td w-20 text-muted')}>
          <Text>{handleReportT(`resultSection.status`)}</Text>
        </View>

        <View style={cls('td p-0 border-0')}></View>

        <View style={cls('td w-30 text-muted')}>
          <Text>{handleReportT(`resultSection.diseaseType`)}</Text>
        </View>
        <View style={cls('td w-20 text-muted')}>
          <Text>{handleReportT(`resultSection.status`)}</Text>
        </View>
      </View>

      {diseaseResultTr(type, Disease.GLAUCOMA, historyIndex)}

      {diseaseResultTr(type, Disease.DR, historyIndex)}

      {diseaseResultTr(type, Disease.AMD, historyIndex)}

      {showNotes && (
        <View style={cls('report-text my-4')}>
          <View style={cls('p-3 gap-1')}>
            <Text>
              {handleReportT(`resultSection.${type}.referToTheEndText`)}
            </Text>

            <Text>
              {handleReportT(`resultSection.${type}.artifactWarningText`)}
            </Text>

            <Text>
              {handleReportT(`resultSection.${type}.thumbnailWarningText`)}
            </Text>

            {historyIndex ? (
              <Text>
                {handleReportT(`resultSection.clinicianEntryText`, {
                  practiceName: practiceName ?? '',
                  timestamp: formattedTimestamp,
                })}
              </Text>
            ) : (
              <Text></Text>
            )}
          </View>
        </View>
      )}
    </>
  ) : (
    <>
      <View style={cls('d-flex hstack justify-content-between')}>
        <View
          style={cls('td w-20 border-top-0', {
            backgroundColor: 'rgb(245, 243, 241)',
          })}
        >
          <Text style={cls('b')}>
            {handleReportT(`resultSection.diseaseType`)}
          </Text>
        </View>
        <View
          style={cls('td w-40 border-top-0 dashed', {
            backgroundColor: 'rgb(245, 243, 241)',
          })}
        >
          <Text style={cls('b')}>
            {handleReportT(`resultSection.${type}.rightEye`)}
          </Text>
        </View>
        <View
          style={cls('td w-40 border-top-0 dashed', {
            backgroundColor: 'rgb(245, 243, 241)',
          })}
        >
          <Text style={cls('b')}>
            {handleReportT(`resultSection.${type}.leftEye`)}
          </Text>
        </View>
      </View>

      {diseaseResultTr(type, Disease.GLAUCOMA, historyIndex)}

      {diseaseResultTr(type, Disease.DR, historyIndex)}

      {diseaseResultTr(type, Disease.AMD, historyIndex)}

      {showNotes && (
        <View style={cls('report-text p-3 gap-1 my-3')}>
          <Text>{handleReportT(`resultSection.${type}.eyeContactText`)}</Text>
          <Text>
            {handleReportT(`resultSection.${type}.artifactWarningText`)}
          </Text>

          <Text>
            {handleReportT(`resultSection.${type}.thumbnailWarningText`)}
          </Text>

          {historyIndex ? (
            <VersionSection
              currentUser={currentUser}
              formattedTimestamp={formattedTimestamp}
            />
          ) : (
            <Text></Text>
          )}
        </View>
      )}
    </>
  );
};

export default ResultSection;
